import type { ImageSourcePropType } from 'react-native';
import type { SpiritualLevelCode } from '../../types/auth';

/**
 * CORE-707 — Canonical v4 is the only level-medallion mapping used by the
 * reusable badge. The files are the untouched 256×256 originals from the
 * approved delivery. Three are already present byte-for-byte in achievements;
 * growth is the only new file. All four paths have no @2x/@3x siblings, so
 * React Native cannot silently select an older density variant.
 */
export const LEVEL_BADGE_ASSETS: Record<SpiritualLevelCode, ImageSourcePropType> = {
  seeker: require('../../../assets/gamification/achievements/sprout.png'),
  keeper: require('../../../assets/gamification/level-badges/canonical-v4/growth.png'),
  master: require('../../../assets/gamification/achievements/soul-light.png'),
  grand_master: require('../../../assets/gamification/achievements/shield-star.png'),
};

/** Canonical user-facing labels from the approved CORE-707 delivery. */
export const LEVEL_BADGE_LABELS: Record<SpiritualLevelCode, string> = {
  seeker: 'Искатель',
  keeper: 'Хранитель',
  master: 'Проводник света',
  grand_master: 'Магистр света',
};

export const LEVEL_BADGE_SIZES = {
  compact: 40,
  card: 48,
  profile: 56,
} as const;

export type LevelBadgeSize = keyof typeof LEVEL_BADGE_SIZES;

/**
 * The outer slot is ten pixels taller than the medallion: 4 px vertical
 * padding and a 1 px border on both sides. Keeping this public makes the
 * loading geometry testable without duplicating magic numbers.
 */
export const LEVEL_BADGE_SLOT_HEIGHTS: Record<LevelBadgeSize, number> = {
  compact: 50,
  card: 58,
  profile: 66,
};

export type LevelBadgeLoadStatus = 'disabled' | 'loading' | 'ready' | 'empty' | 'error';

/** Disabled means the feature/user is out of scope; every other state keeps its slot. */
export const shouldReserveLevelBadgeSlot = (status: LevelBadgeLoadStatus): boolean =>
  status !== 'disabled';

export const isSpiritualLevelCode = (value: unknown): value is SpiritualLevelCode =>
  value === 'seeker'
  || value === 'keeper'
  || value === 'master'
  || value === 'grand_master';

/** Unknown/missing codes deliberately render no badge — never a fake seeker. */
export const resolveLevelBadgeAsset = (value: unknown): ImageSourcePropType | null =>
  isSpiritualLevelCode(value) ? LEVEL_BADGE_ASSETS[value] : null;

/** Technical backend codes never leak into UI copy. */
export const resolveLevelBadgeLabel = (value: unknown): string | null =>
  isSpiritualLevelCode(value) ? LEVEL_BADGE_LABELS[value] : null;

/** Longest approved label; an invisible copy reserves the exact loaded width. */
export const LEVEL_BADGE_SIZER_LABEL = LEVEL_BADGE_LABELS.master;
